/**
 * useSignOutAllStaffSessions — TanStack mutation over
 * `DELETE /admin/staff/:userId/sessions`.
 *
 * Revokes every active session for a platform-staff member. Available
 * to root + support (`staff:sign_out_all` maps to both roles — see
 * `shared/auth/admin-permissions.ts`), subject to two server-side
 * guardrails: support may not target a root member (403), and no actor
 * may target themselves (422). Success is 200 with a
 * `{ revokedCount: number }` body.
 *
 * Like useResetStaff2fa this surfaces a structured RESULT rather than
 * throwing on the non-success branches — every outcome is a UI state
 * the caller renders inline (the success branch announces the revoked
 * count via aria-live).
 *
 * Result union:
 *   - 'success'    — 200; carries `revokedCount` from the response body.
 *   - 'notFound'   — 404 (anti-enum; not a platform member).
 *   - 'forbidden'  — 403 (support attempted to target a root member).
 *   - 'cannotSelf' — 422 (actor targeted themselves).
 *   - 'error'      — anything else.
 *
 * Does NOT invalidate the staff list — revoking sessions does not
 * mutate any staff-list field.
 */

import { useMutation } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';

export type SignOutAllStaffSessionsResult =
  | { readonly kind: 'success'; readonly revokedCount: number }
  | { readonly kind: 'notFound' }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'cannotSelf' }
  | { readonly kind: 'error' };

export interface UseSignOutAllStaffSessionsResult {
  readonly mutateAsync: (
    userId: string,
  ) => Promise<SignOutAllStaffSessionsResult>;
  readonly isPending: boolean;
}

interface RevokeSessionsEnvelope {
  readonly revokedCount: number;
}

export function useSignOutAllStaffSessions(): UseSignOutAllStaffSessionsResult {
  const mutation = useMutation<SignOutAllStaffSessionsResult, never, string>({
    mutationFn: async (userId) => {
      const { response, data } = await apiClient.DELETE(
        '/admin/staff/{userId}/sessions',
        { params: { path: { userId } } },
      );
      if (response.ok && data !== undefined) {
        return {
          kind: 'success',
          revokedCount: (data as RevokeSessionsEnvelope).revokedCount,
        };
      }
      if (response.status === 404) {
        return { kind: 'notFound' };
      }
      if (response.status === 403) {
        return { kind: 'forbidden' };
      }
      if (response.status === 422) {
        return { kind: 'cannotSelf' };
      }
      return { kind: 'error' };
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}
